import {canOpenReportBuilder} from '../../../components/ai-report-builder/reportBuilderGate';
import {EXTERNAL_SHARE_TYPE} from '../../shared/util/ReportServiceUtil';

/** The Reports list records where a report was authored so it can reopen the report there. Same
 * value the list's open action reads. */
const AI_SOURCE = 'AI';

/** Where a saved report should open instead of the standard report page, or null to open it here.
 *
 * A report authored in the AI report builder is stored in the standard saved-report shape so the
 * Reports list and sharing work unchanged, but its columns, filters and groupings use the builder's
 * own vocabulary. The standard pages were not written for that vocabulary: a task report grouped
 * by a token they do not know crashes them outright, and a project-status report has no standard
 * page at all. The Reports list already sends AI reports to the builder; this applies the same rule
 * on the routes that bypass the list — share links and direct saved-report URLs — so a recipient
 * reaches the builder instead of a page that cannot render the report.
 *
 * The gate is re-checked rather than trusted from the origin, exactly as the list does: the builder
 * can be switched off after a report is made, and a redirect into a surface that then bounces home
 * would strand the report. When the gate is closed the caller falls back to the standard page,
 * which must therefore degrade rather than crash for such a report. `id` is already the Relay
 * global id the builder route expects.
 *
 * An `EXTERNAL_SHARE_TYPE` share is the one case where access can come from the share alone rather
 * than from the viewer's own report-level permission — most visibly a cross-company recipient. The
 * builder route (`SavedReportBuilder` / `HttpReportDataSource`) has no concept of a share at all — it
 * loads a report definition straight by id, with nowhere to pass a share even if this function
 * returned one — so redirecting there would lose that access. The standard page keeps rendering such
 * a share correctly (the grouping normaliser now makes that safe even for an AI report), so an
 * external share is deliberately left off the redirect.
 *
 * An `INTERNAL_SHARE_TYPE` share carries no such gap — the recipient is a same-company Forecast user
 * whose own permissions and feature flags are what `canOpenReportBuilder()` already checks — so it
 * still redirects, which is the behaviour this function exists for in the first place. */
export function reportBuilderPathFor(
	savedReport: {id: string; source?: string | null},
	shareType?: string | null
): string | null {
	if (savedReport.source !== AI_SOURCE || shareType === EXTERNAL_SHARE_TYPE || !canOpenReportBuilder()) {
		return null;
	}
	return `/report-builder/${savedReport.id}`;
}
